import React, { useContext, useEffect, useRef, useState } from 'react';

import isMatch from 'lodash/isMatch';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';
import { LegacyContext } from '~/stormpath/components/LegacyContextType';
import { AuthContext } from '~/stormpath/components/Router';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Button, Container } from '@mui/material';

import { fetchOrderAndPurchaserData, fetchOrderData, submitUpdatesForm } from '../../../actions/order';
import { fetchUserData } from '../../../actions/user';
import { EDIT_ORDER_TITLE, OrderPatchOperations } from '../../../consts/order';
import { orderSelector } from '../../../selectors/orderSelector';
import { userSelector } from '../../../selectors/userSelector';
import { createPatchPayload } from '../../../utils/createPatchPayload';
import OrderForm from '../../Common/Forms/OrderForm';
import Spinner from '../../Common/Spinner';
import { withTenant } from '../../hoc';

type OrderEditContainerState = {
  fk_purchaser_id: {
    op: OrderPatchOperations;
    value: string;
  };
};

function OrderEditContainer(props) {
  const { user } = useContext<LegacyContext>(AuthContext);
  const { id_orders: orderId } = useParams<{ id_orders: string }>();
  const { fetchUserData, fetchOrderAndPurchaserData, submitUpdatesForm } = props;
  const { error, getPurchaser, isLoading, isUpdating, order, tenant } = props;

  const history = useHistory();
  // TODO: Fix this {}. Setting this undefined/null is causing runtime issues.
  const [state, setState] = useState<OrderEditContainerState>({} as OrderEditContainerState);

  const prevUserContextRef = useRef(null);

  const handleChange = (e, op) => {
    setState((prevState) => ({
      ...prevState,
      [e.target.name]: { op, value: e.target.value },
    }));
  };

  const handleChangePurchaser = async (op: OrderPatchOperations, purchaserId: string) => {
    await fetchUserData(purchaserId);
    setState((prevState) => ({
      ...prevState,
      fk_purchaser_id: { op, value: purchaserId },
    }));
  };

  const submitOrderEditUpdatesForm = async () => {
    const payload = createPatchPayload(state ?? {});
    await submitUpdatesForm(orderId, payload);
    history.push(`/purchases/${orderId}`);
  };

  const isDirty = (order: App.Order) => {
    const stateEntries = Object.entries(state);
    const orderUpdates = {};
    for (const entry of stateEntries) {
      orderUpdates[entry[0]] = entry[1].value;
    }
    return !isMatch(order, orderUpdates);
  };

  useEffect(() => {
    if (user) {
      prevUserContextRef.current = user;
      fetchOrderAndPurchaserData(orderId);
    }
  }, [user, orderId, fetchOrderAndPurchaserData]);

  useEffect(() => {
    if (prevUserContextRef.current && !isMatch(prevUserContextRef.current, user)) {
      prevUserContextRef.current = user;
      fetchOrderAndPurchaserData(orderId);
    }
  }, [user, orderId, fetchOrderAndPurchaserData]);

  return (
    <Container maxWidth="xl">
      <Button
        component={Link}
        startIcon={<ChevronLeftIcon />}
        to={`/purchases/${orderId}`}
        style={{ display: 'inline-flex' }}
      >
        Return to order
      </Button>
      <h1 className="page-header">{EDIT_ORDER_TITLE}</h1>

      {isLoading ? (
        <Spinner />
      ) : (
        <OrderForm
          order={
            order // temp changes are saved to react state
          }
          purchaser={state.fk_purchaser_id ? getPurchaser(state.fk_purchaser_id.value) : order.purchaser}
          handleChange={handleChange}
          handleChangePurchaser={handleChangePurchaser}
          tenant={tenant}
          onSave={submitOrderEditUpdatesForm}
          isLoading={isLoading}
          isUpdating={isUpdating}
          isDirty={isDirty(order)}
          error={error}
        />
      )}
    </Container>
  );
}

const mapStateToProps = (state, ownProps) => {
  const { match } = ownProps;
  const orderId = match.params.id_orders;
  const order = orderSelector(state, orderId);
  const getPurchaser = (purchaserId: string) => userSelector(state, purchaserId);
  const { isLoading: isLoadingOrder, isUpdating, error: orderError } = state.order;
  const { isLoading: isLoadingUser, error: userError } = state.user;
  return {
    error: userError || orderError,
    getPurchaser,
    isLoading: isLoadingOrder || isLoadingUser,
    isUpdating,
    order,
  };
};

const mapDispatchToProps = {
  fetchOrderData,
  fetchOrderAndPurchaserData,
  submitUpdatesForm,
  fetchUserData,
};

export default withTenant(connect(mapStateToProps, mapDispatchToProps)(OrderEditContainer));
